/**
 * Duplicate Management Dashboard
 *
 * Admin / manager view of every duplicate-related event across Direct
 * Sales, Indirect Sales and Broker Hub. Mounted at
 *   /admin/duplicate-management
 *
 * Three things live here:
 *
 *  1. KPI strip — total duplicates detected, cross-channel customers,
 *     pending approvals, approved, rejected.
 *  2. Approval queue — every duplicate_request waiting on a manager's
 *     decision. Each row has Approve / Reject buttons.
 *  3. Recent decisions — the approved / rejected tabs for context
 *     (with reviewer name + timestamp + notes).
 *
 * The page is purely a render of `/api/customers/dashboard/metrics`
 * and `/api/customers/duplicate-requests` — all heavy lifting is
 * server-side so refreshes are cheap.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogBody, DialogFooter,
} from '@/components/ui/dialog';
import {
  Shield, AlertTriangle, Users, CheckCircle2, XCircle, Clock,
  Edit3, AlertOctagon, RefreshCw, Loader2, Eye, ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import InfoBadge from '@/components/InfoBadge';
import {
  fetchDuplicateDashboard,
  listDuplicateRequests,
  approveDuplicateRequest,
  rejectDuplicateRequest,
} from './api/customerMaster';

const REQUEST_TYPE_META = {
  detail_update: {
    label: 'Detail update',
    icon: Edit3,
    tone: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  force_duplicate: {
    label: 'Force duplicate',
    icon: AlertOctagon,
    tone: 'bg-rose-50 text-rose-700 border-rose-200',
  },
};

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : '—');

export default function DuplicateManagementDashboard() {
  const { token } = useAuth();
  const { isDarkMode } = useTheme();

  const [metrics, setMetrics] = useState(null);
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Review dialog — opens when an admin clicks Approve/Reject on a
  // row. The dialog captures the optional reviewer note before
  // hitting the API.
  const [reviewTarget, setReviewTarget] = useState(null);  // { request, mode: 'approve'|'reject' }
  const [reviewNote, setReviewNote] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);

  const reload = useCallback(async () => {
    if (!token) return;
    setRefreshing(true);
    try {
      const [m, p, a, r] = await Promise.all([
        fetchDuplicateDashboard(token),
        listDuplicateRequests(token, 'pending'),
        listDuplicateRequests(token, 'approved'),
        listDuplicateRequests(token, 'rejected'),
      ]);
      setMetrics(m);
      setPending(p?.requests || []);
      setApproved(a?.requests || []);
      setRejected(r?.requests || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load the dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => { reload(); }, [reload]);

  const submitReview = async () => {
    if (!reviewTarget) return;
    setSubmittingReview(true);
    try {
      if (reviewTarget.mode === 'approve') {
        await approveDuplicateRequest(token, reviewTarget.request.id, reviewNote);
        toast.success('Request approved');
      } else {
        await rejectDuplicateRequest(token, reviewTarget.request.id, reviewNote);
        toast.success('Request rejected');
      }
      setReviewTarget(null);
      setReviewNote('');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit the review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const totals = metrics?.totals || {};
  const byTypePending = metrics?.pending_by_type || {};

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#0a0a0f] text-white' : 'bg-slate-50'}`}>
      <div className="mx-auto max-w-[1500px] space-y-5 p-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold">
              <Shield className="text-violet-500" size={26} />
              Duplicate Management
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Approve customer detail updates, decide on force-duplicate requests,
              and track every cross-channel duplicate the system catches.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={reload} disabled={refreshing}>
            <RefreshCw size={14} className={`mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* KPI strip */}
        {loading ? (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24 w-full" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <KpiTile
              icon={AlertTriangle}
              tone="slate"
              label="Total duplicates detected"
              value={totals.total_duplicates_detected ?? 0}
              sub="across all channels"
              info="Lifetime count of every duplicate-attempt event logged by the system: blocked attempts, manager-approved duplicates, rejected duplicates, and successful lead-to-customer merges."
            />
            <KpiTile
              icon={Users}
              tone="violet"
              label="Cross-channel customers"
              value={totals.cross_channel_duplicates ?? 0}
              sub="2+ source channels"
              info="Customers whose opportunities span more than one acquisition channel (e.g. came in via Direct Sales AND a Broker Hub firm). Higher numbers = healthy multi-touch funnels."
            />
            <KpiTile
              icon={Clock}
              tone="amber"
              label="Pending approval"
              value={totals.pending_approval ?? 0}
              sub={`${byTypePending.detail_update || 0} update · ${byTypePending.force_duplicate || 0} force`}
              urgent={(totals.pending_approval ?? 0) > 0}
              info="Requests waiting on a manager decision — either detail-update requests (broker wants to change phone/email/etc.) or force-duplicate requests (deliberate parallel lead). Action: review and approve/reject."
            />
            <KpiTile
              icon={CheckCircle2}
              tone="emerald"
              label="Approved"
              value={totals.approved ?? 0}
              info="Total requests a manager has approved historically. Each approval applied an actual change (a detail update or a parallel lead insertion), all audit-logged."
            />
            <KpiTile
              icon={XCircle}
              tone="rose"
              label="Rejected"
              value={totals.rejected ?? 0}
              info="Requests a manager declined. The original record was left as-is and the requester saw the rejection note. Use the Decision History tab below to audit the reasoning."
            />
          </div>
        )}

        {/* Approval queue */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock size={16} className="text-amber-500" />
              Approval queue
              {pending.length > 0 && (
                <Badge className="ml-1 bg-rose-500 text-white text-[10px]">
                  {pending.length} pending
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              Detail updates and force-duplicate requests waiting on a decision.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <RequestList
              rows={pending}
              loading={loading}
              emptyLabel="No pending requests — every duplicate has been decided."
              showActions
              onApprove={(req) => { setReviewTarget({ request: req, mode: 'approve' }); setReviewNote(''); }}
              onReject={(req) => { setReviewTarget({ request: req, mode: 'reject' }); setReviewNote(''); }}
            />
          </CardContent>
        </Card>

        {/* Decided history */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Decision history</CardTitle>
            <CardDescription>Recent approvals and rejections, with reviewer + notes.</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="approved">
              <TabsList>
                <TabsTrigger value="approved" className="text-xs">
                  Approved
                  <Badge variant="outline" className="ml-1.5 text-[10px]">{approved.length}</Badge>
                </TabsTrigger>
                <TabsTrigger value="rejected" className="text-xs">
                  Rejected
                  <Badge variant="outline" className="ml-1.5 text-[10px]">{rejected.length}</Badge>
                </TabsTrigger>
              </TabsList>
              <TabsContent value="approved" className="mt-3">
                <RequestList
                  rows={approved}
                  loading={loading}
                  emptyLabel="No approved requests yet."
                />
              </TabsContent>
              <TabsContent value="rejected" className="mt-3">
                <RequestList
                  rows={rejected}
                  loading={loading}
                  emptyLabel="No rejected requests yet."
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Review confirmation dialog */}
      <Dialog open={!!reviewTarget} onOpenChange={(o) => !o && setReviewTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {reviewTarget?.mode === 'approve' ? 'Approve request' : 'Reject request'}
            </DialogTitle>
            <DialogDescription>
              {reviewTarget?.mode === 'approve'
                ? 'This will apply the change immediately and log it on the audit trail.'
                : 'The requester will see this was rejected. Please leave a note explaining why.'}
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <Textarea
              rows={3}
              value={reviewNote}
              onChange={(e) => setReviewNote(e.target.value)}
              placeholder={reviewTarget?.mode === 'approve'
                ? 'Optional note (visible on the audit trail)'
                : 'Why is this being rejected?'}
            />
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewTarget(null)} disabled={submittingReview}>
              Cancel
            </Button>
            <Button
              onClick={submitReview}
              disabled={submittingReview || (reviewTarget?.mode === 'reject' && !reviewNote.trim())}
              className={reviewTarget?.mode === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'}
            >
              {submittingReview && <Loader2 size={14} className="mr-1.5 animate-spin" />}
              {reviewTarget?.mode === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ----------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------

const KPI_TONE = {
  slate:   { bg: 'bg-slate-50',   text: 'text-slate-700',   ring: 'ring-slate-200' },
  violet:  { bg: 'bg-violet-50',  text: 'text-violet-700',  ring: 'ring-violet-200' },
  amber:   { bg: 'bg-amber-50',   text: 'text-amber-700',   ring: 'ring-amber-200' },
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200' },
  rose:    { bg: 'bg-rose-50',    text: 'text-rose-700',    ring: 'ring-rose-200' },
};

const KpiTile = ({ icon: Icon, tone = 'slate', label, value, sub, urgent = false, info }) => {
  const t = KPI_TONE[tone] || KPI_TONE.slate;
  return (
    <div className={`rounded-lg border bg-white p-3 ring-1 ${urgent ? 'border-rose-300 ring-rose-200' : `border-slate-200 ${t.ring}`}`}>
      <div className="flex items-center gap-2">
        <span className={`inline-flex h-7 w-7 items-center justify-center rounded-md ${t.bg} ${t.text}`}>
          <Icon size={14} />
        </span>
        <span className="flex-1 text-[11px] font-medium text-slate-500">{label}</span>
        <InfoBadge text={info} />
      </div>
      <div className="mt-1.5 text-2xl font-bold text-slate-800">{value}</div>
      {sub && <div className="text-[11px] text-slate-400">{sub}</div>}
    </div>
  );
};

const RequestList = ({ rows, loading, emptyLabel, showActions = false, onApprove, onReject }) => {
  if (loading) {
    return <Skeleton className="h-24 w-full" />;
  }
  if (!rows || rows.length === 0) {
    return <div className="rounded-md border border-dashed border-slate-300 bg-white px-4 py-8 text-center text-sm text-slate-500">{emptyLabel}</div>;
  }
  return (
    <ul className="space-y-2">
      {rows.map((r) => (
        <RequestRow
          key={r.id}
          request={r}
          showActions={showActions}
          onApprove={onApprove}
          onReject={onReject}
        />
      ))}
    </ul>
  );
};

const RequestRow = ({ request, showActions, onApprove, onReject }) => {
  const meta = REQUEST_TYPE_META[request.request_type] || REQUEST_TYPE_META.detail_update;
  const Icon = meta.icon;
  return (
    <li className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="flex items-start gap-3">
        <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border ${meta.tone}`}>
          <Icon size={15} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className={`text-[10px] ${meta.tone}`}>
              {meta.label}
            </Badge>
            <span className="text-sm font-medium text-slate-800">
              Requested by {request.requested_by_name || 'someone'}
            </span>
            <span className="text-[11px] text-slate-500">
              · {fmtDate(request.requested_at)}
            </span>
          </div>

          {/* Body — diff for detail_update, lead-payload summary for force_duplicate */}
          {request.request_type === 'detail_update' && (
            <div className="mt-2 space-y-1">
              {(request.field_changes || []).map((ch, i) => (
                <div key={i} className="text-xs text-slate-700">
                  <span className="font-semibold text-slate-600">{ch.field}: </span>
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 line-through text-slate-500">
                    {String(ch.old_value || '—')}
                  </span>
                  <ArrowRight size={11} className="mx-1.5 inline text-slate-400" />
                  <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-emerald-700">
                    {String(ch.new_value || '—')}
                  </span>
                </div>
              ))}
            </div>
          )}
          {request.request_type === 'force_duplicate' && (
            <div className="mt-2 text-xs text-slate-600">
              New lead would be inserted into{' '}
              <code className="rounded bg-slate-100 px-1 py-0.5">{request.new_lead_target}</code>
              {request.new_lead_payload?.client_name
                ? <> · client <strong>{request.new_lead_payload.client_name}</strong></>
                : null}
              {request.new_lead_payload?.project_name
                ? <> · project <strong>{request.new_lead_payload.project_name}</strong></>
                : null}
            </div>
          )}

          {request.reason && (
            <div className="mt-2 rounded bg-slate-50 px-2 py-1.5 text-[11px] text-slate-600">
              <strong className="text-slate-700">Reason:</strong> {request.reason}
            </div>
          )}

          {/* For decided rows, surface the reviewer info */}
          {request.status !== 'pending' && (
            <div className="mt-2 text-[11px] text-slate-500">
              {request.status === 'approved' ? 'Approved' : 'Rejected'} by{' '}
              <strong>{request.reviewed_by_name || 'admin'}</strong>{' '}
              · {fmtDate(request.reviewed_at)}
              {request.review_notes && <> · "{request.review_notes}"</>}
            </div>
          )}
        </div>

        {showActions && (
          <div className="flex shrink-0 flex-col gap-1.5">
            <Button
              size="sm"
              className="h-7 bg-emerald-600 hover:bg-emerald-700"
              onClick={() => onApprove?.(request)}
            >
              <CheckCircle2 size={12} className="mr-1" /> Approve
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 border-rose-300 text-rose-600 hover:bg-rose-50"
              onClick={() => onReject?.(request)}
            >
              <XCircle size={12} className="mr-1" /> Reject
            </Button>
          </div>
        )}
      </div>
    </li>
  );
};
